/**
 * EVERY BYTE NOTITIA PUTS ON THE USER'S DEVICE, DECLARED IN ONE TABLE.
 *
 * Web Storage used to be reached by direct `localStorage.*` calls from nine
 * places under three naming conventions, and two of those places took their key
 * from a prop or an argument — so the set of keys the app writes was defined by
 * its call sites and could not be enumerated at all. Nothing could state what
 * was stored, and nothing could clear it.
 *
 * This module is the inventory. Each key is a `StorageEntry` declared below;
 * everything else in the app goes through the accessors, which is enforced by
 * lint (see `eslint.config.js`). That is what lets the privacy panel render the
 * real list rather than a hand-kept copy of it, and what makes "clear my local
 * data" complete by construction.
 *
 * The boundary rule (`import-x/no-restricted-paths`) forbids `shared/` from
 * importing a section, so section-specific keys are declared HERE and the
 * sections import them back. That constraint is the feature: a section cannot
 * add a key without adding it to this table first.
 *
 * Three properties every entry has:
 *
 * - **`parse` is total.** It takes the raw string and returns `undefined` for
 *   anything it does not recognise, which the reader treats as "discard and use
 *   the fallback". Stored shapes drift as the app changes — a sort mode we no
 *   longer ship, a width written by an older build — and silently discarding an
 *   unreadable value is what keeps a schema change from breaking a returning
 *   user.
 * - **Every access is guarded.** Storage can be disabled (Safari private mode
 *   throws on `setItem`) or full. A failure degrades to the fallback instead of
 *   throwing into a component's setup.
 * - **A `purpose`,** which is what `clearByPurpose` and the privacy panel sort
 *   by: `essential` is the session itself, `preference` is device ergonomics
 *   (theme, locale, pane widths), `view-state` is what you were looking at
 *   (searches, filters, sort, tabs).
 */
import { PANEL_ALIGNS, PANEL_COLUMNS, type PanelColumn } from "@/shared/utils/panelPlacement";

export type StoragePurpose = "essential" | "preference" | "view-state";

export interface StorageEntry<T> {
	/** Stable identity, and the suffix of this entry's i18n key in the privacy panel. */
	id: string;
	/** `notitia:<area>:<name>`, plus `:<scopeId>` when `scoped`. */
	key: string;
	purpose: StoragePurpose;
	/** One stored value per scope id (a project, a process, a dataspace). */
	scoped?: boolean;
	/** TOTAL — `undefined` means "this is not something we can read; discard it". */
	parse: (raw: string) => T | undefined;
	serialize: (value: T) => string;
	fallback: T;
}

export interface StorageAccessor<T> {
	readonly entry: StorageEntry<T>;
	/** The stored value, or the fallback when absent, unreadable or unavailable. */
	read(scope?: string): T;
	write(value: T, scope?: string): void;
	/** One scope, or — for a scoped entry called without one — all of them. */
	clear(scope?: string): void;
}

// ── Guarded primitives ────────────────────────────────────────────────────────
// The only place in the app that touches Web Storage. Each one swallows the
// failure and reports "nothing there", so a browser with storage disabled makes
// every read a fallback and every write a no-op rather than an exception.

function safeGet(key: string): string | null {
	try {
		return localStorage.getItem(key);
	} catch {
		return null;
	}
}

function safeSet(key: string, value: string): void {
	try {
		localStorage.setItem(key, value);
	} catch {
		/* disabled or full — the value just won't outlive the session */
	}
}

function safeRemove(key: string): void {
	try {
		localStorage.removeItem(key);
	} catch {
		/* nothing to do if we cannot even remove */
	}
}

function safeKeys(): string[] {
	try {
		return Object.keys(localStorage);
	} catch {
		return [];
	}
}

// ── Scoped values ─────────────────────────────────────────────────────────────

/**
 * A scoped value carries the time it was written, so the cap below can evict the
 * least recently used one without a separate index key to keep in step. Values
 * an older build wrote raw, or anything else unparseable, read as absent.
 */
interface ScopedEnvelope {
	v: string;
	t: number;
}

function readEnvelope(raw: string | null): ScopedEnvelope | null {
	if (raw === null) return null;
	try {
		const parsed: unknown = JSON.parse(raw);
		if (typeof parsed !== "object" || parsed === null) return null;
		const { v, t } = parsed as Partial<ScopedEnvelope>;
		return typeof v === "string" && typeof t === "number" ? { v, t } : null;
	} catch {
		return null;
	}
}

/**
 * How many scopes one entry may hold. Scopes multiply — a search term per
 * project, a tab per process — so without a cap this grows for as long as the
 * user keeps opening things. Evicting the least recently written keeps the
 * entries that are actually in use and bounds the whole registry at
 * `entries × SCOPE_LIMIT`.
 */
const SCOPE_LIMIT = 20;

function scopeKeysOf(entry: StorageEntry<unknown>): string[] {
	const prefix = `${entry.key}:`;
	return safeKeys().filter((key) => key.startsWith(prefix));
}

function pruneScopes(entry: StorageEntry<unknown>): void {
	const held = scopeKeysOf(entry);
	if (held.length <= SCOPE_LIMIT) return;
	held.map((key) => ({ key, t: readEnvelope(safeGet(key))?.t ?? 0 }))
		.sort((a, b) => b.t - a.t)
		.slice(SCOPE_LIMIT)
		.forEach(({ key }) => safeRemove(key));
}

// ── The registry ──────────────────────────────────────────────────────────────

const ENTRIES: StorageEntry<unknown>[] = [];

function register<T>(entry: StorageEntry<T>): StorageAccessor<T> {
	ENTRIES.push(entry as StorageEntry<unknown>);

	/** `undefined` for a scoped entry whose scope isn't known yet — read and write both no-op. */
	function keyFor(scope: string | undefined): string | undefined {
		if (!entry.scoped) return entry.key;
		return scope ? `${entry.key}:${scope}` : undefined;
	}

	return {
		entry,
		read(scope?: string): T {
			const key = keyFor(scope);
			if (!key) return entry.fallback;
			const raw = entry.scoped ? (readEnvelope(safeGet(key))?.v ?? null) : safeGet(key);
			if (raw === null) return entry.fallback;
			return entry.parse(raw) ?? entry.fallback;
		},
		write(value: T, scope?: string): void {
			const key = keyFor(scope);
			if (!key) return;
			const serialized = entry.serialize(value);
			if (!entry.scoped) {
				safeSet(key, serialized);
				return;
			}
			safeSet(key, JSON.stringify({ v: serialized, t: Date.now() } satisfies ScopedEnvelope));
			pruneScopes(entry as StorageEntry<unknown>);
		},
		clear(scope?: string): void {
			const key = keyFor(scope);
			if (key) {
				safeRemove(key);
				return;
			}
			scopeKeysOf(entry as StorageEntry<unknown>).forEach(safeRemove);
		},
	};
}

/** The inventory, in declaration order — what the privacy panel renders. */
export function listEntries(): ReadonlyArray<StorageEntry<unknown>> {
	return ENTRIES;
}

/**
 * Characters currently held for an entry, summed over its scopes. Key length
 * counts: for a scoped entry the keys are most of the footprint.
 */
export function storedSize(entry: StorageEntry<unknown>): number {
	const keys = entry.scoped ? scopeKeysOf(entry) : [entry.key];
	return keys.reduce((total, key) => {
		const value = safeGet(key);
		// Absent contributes nothing — not even its key length, or every entry
		// the user has never touched would read as a nonzero row in the panel.
		return value === null ? total : total + key.length + value.length;
	}, 0);
}

/** How many scopes an entry is holding — 0 for an unscoped one. */
export function storedScopeCount(entry: StorageEntry<unknown>): number {
	return entry.scoped ? scopeKeysOf(entry).length : 0;
}

/**
 * Drops every key of the named purposes. Nothing is cleared implicitly: a caller
 * that wants the session gone has to say `essential`, which is what keeps
 * "clear my searches" from signing anybody out.
 */
export function clearByPurpose(...purposes: StoragePurpose[]): void {
	for (const entry of ENTRIES) {
		if (!purposes.includes(entry.purpose)) continue;
		if (entry.scoped) scopeKeysOf(entry).forEach(safeRemove);
		else safeRemove(entry.key);
	}
}

export function clearAll(): void {
	clearByPurpose("essential", "preference", "view-state");
}

// ── Codecs ────────────────────────────────────────────────────────────────────
// Each one supplies `parse` + `serialize`; the entry adds the fallback.

const text = {
	parse: (raw: string): string | undefined => raw,
	// `?? ""` guards a type lie with real consequences: Vuetify's `clearable`
	// sets a field's model to `null`, and a null reaching `setItem` is stored as
	// the string "null" — which reads back as a search term the user never typed.
	// The bound components normalize it too; this is the backstop.
	serialize: (value: string): string => value ?? "",
};

/** Discards anything outside the set — a mode or tab id we no longer ship. */
function oneOf<T extends string>(values: readonly T[]) {
	return {
		parse: (raw: string): T | undefined =>
			(values as readonly string[]).includes(raw) ? (raw as T) : undefined,
		serialize: (value: T): string => value,
	};
}

/**
 * A yes/no preference, stored as `on`/`off` rather than `true`/`false`.
 *
 * The privacy panel shows the raw value, and a person reading their own stored
 * data should not have to know which side of a boolean the app calls "on".
 */
const flag = {
	parse: (raw: string): boolean | undefined =>
		raw === "on" ? true : raw === "off" ? false : undefined,
	serialize: (value: boolean): string => (value ? "on" : "off"),
};

/** Clamps rather than discards: a width from a bigger window is still a width. */
function clampedInt(min: number, max: number) {
	return {
		parse: (raw: string): number | undefined => {
			const parsed = Number.parseInt(raw, 10);
			return Number.isFinite(parsed) ? Math.min(Math.max(parsed, min), max) : undefined;
		},
		serialize: (value: number): string => String(value),
	};
}

/**
 * JSON, read through `read`, which takes the parsed value and gives the stored value back or
 * `undefined` for a shape it does not recognise — and so does text that is not JSON at all. `write`
 * shapes the value on the way out (a list cut to its cap). One place for the try/catch every JSON
 * entry needs, so none of them can forget that an older build or a hand edit wrote something else.
 */
function jsonCodec<T>(
	read: (value: unknown) => T | undefined,
	write: (value: T) => unknown = (v) => v,
) {
	return {
		parse: (raw: string): T | undefined => {
			try {
				return read(JSON.parse(raw));
			} catch {
				return undefined;
			}
		},
		serialize: (value: T): string => JSON.stringify(write(value)),
	};
}

/** A JSON array of strings, or nothing. */
const stringList = (value: unknown): string[] | undefined =>
	Array.isArray(value) && value.every((item) => typeof item === "string") ? value : undefined;

/** A plain object — not an array, not null — or nothing. */
const record = (value: unknown): Record<string, unknown> | undefined =>
	typeof value === "object" && value !== null && !Array.isArray(value)
		? (value as Record<string, unknown>)
		: undefined;

/** A CSS length as SidebarShell writes it — `"32.500%"` or `"300px"`. */
const cssLength = {
	parse: (raw: string): string | undefined =>
		/^\d+(\.\d+)?(px|%|rem|em)$/.test(raw.trim()) ? raw.trim() : undefined,
	serialize: (value: string): string => value,
};

// ── Essential ─────────────────────────────────────────────────────────────────

/**
 * Which key scheme the device is on, so the rename below runs exactly once.
 * Bookkeeping rather than user data, but it is a key we write, so it is in the
 * table like everything else.
 */
const storageVersion = register<string>({
	id: "storageVersion",
	key: "notitia:storage:version",
	purpose: "essential",
	...text,
	fallback: "",
});

/** An invitation someone set out to accept, and when. See `shared/utils/pendingInvitation.ts`. */
export interface PendingInvitation {
	path: string;
	savedAt: number;
}

/**
 * The invitation a signed-out invitee clicked "Sign in to accept" on. Creating an account at
 * the provider does not return to the sign-in that started it, so the next sign-in in this
 * browser opens the invitation from here, once. Written only by that click.
 */
const pendingInvitation = register<PendingInvitation | null>({
	id: "pendingInvitation",
	key: "notitia:auth:pendingInvitation",
	purpose: "essential",
	...jsonCodec<PendingInvitation | null>((value) => {
		const fields = record(value);
		if (!fields) return undefined;
		const { path, savedAt } = fields;
		if (typeof path !== "string" || !/^\/invitations\/[^/?#]+$/.test(path)) return undefined;
		if (typeof savedAt !== "number" || !Number.isFinite(savedAt)) return undefined;
		return { path, savedAt };
	}),
	fallback: null,
});

// ── Preferences ───────────────────────────────────────────────────────────────

const themeMode = register<"light" | "dark" | "system">({
	id: "themeMode",
	key: "notitia:app:themeMode",
	purpose: "preference",
	...oneOf(["light", "dark", "system"] as const),
	fallback: "system",
});

const locale = register<string>({
	id: "locale",
	key: "notitia:app:locale",
	purpose: "preference",
	// The locale list lives in `i18n/messages.ts`, which this module may not
	// import without a cycle (i18n reads storage). `isLocale` narrows on the
	// way out instead — the entry only has to keep the string intact.
	...text,
	fallback: "",
});

const logHeight = register<number>({
	id: "logHeight",
	key: "notitia:process:logHeight",
	purpose: "preference",
	...clampedInt(120, 2000),
	fallback: 220,
});

/**
 * How a process view divides itself, as the reader dragged it: each column's weight in the body
 * (`cols`) and, for a column with a top and a bottom slot, the top's share of its height (`rows`). One
 * per process DEFINITION (scope `<dataspace>/<definition>`): how much room you give a call's chat is
 * how you work that kind of process, and the next instance opens the same. Only what was dragged is
 * held; a column without a weight takes its equal share (`columnWidths`), a row without one is half
 * and half (`rowSplit`). Written when a drag ends, never while it moves.
 */
export interface PanelSplits {
	cols: Partial<Record<PanelColumn, number>>;
	rows: Partial<Record<PanelColumn, number>>;
}

/** The numbers of `value` the three columns have, each held to `min`–`max`; nothing else is kept. */
function columnNumbers(
	value: unknown,
	min: number,
	max: number,
): Partial<Record<PanelColumn, number>> {
	const fields = record(value) ?? {};
	const out: Partial<Record<PanelColumn, number>> = {};
	for (const column of PANEL_COLUMNS) {
		const n = fields[column];
		if (typeof n === "number" && Number.isFinite(n))
			out[column] = Math.min(Math.max(n, min), max);
	}
	return out;
}

const operatorPanelSplits = register<PanelSplits>({
	id: "operatorPanelSplits",
	key: "notitia:process:panelSplits",
	purpose: "preference",
	scoped: true,
	...jsonCodec<PanelSplits>((value) => {
		const fields = record(value);
		if (!fields) return undefined;
		return {
			cols: columnNumbers(fields.cols, 0.02, 1),
			rows: columnNumbers(fields.rows, 0.05, 0.95),
		};
	}),
	fallback: { cols: {}, rows: {} },
});

const envNameColWidth = register<number>({
	id: "envNameColWidth",
	key: "notitia:dataspace:envNameColWidth",
	purpose: "preference",
	...clampedInt(100, 480),
	fallback: 200,
});

/**
 * The resizable sidebar layers, keyed by id rather than by a free-form string:
 * `SidebarShell`'s `storageKey` prop is this object's key type, so a new sidebar
 * cannot invent a key that the inventory does not know about — it has to be
 * added here first.
 */
export const SIDEBAR_WIDTH = {
	processOverview: register<string>({
		id: "processOverviewWidth",
		key: "notitia:sidebar:processOverviewWidth",
		purpose: "preference",
		...cssLength,
		fallback: "",
	}),
	processProperties: register<string>({
		id: "processPropertiesWidth",
		key: "notitia:sidebar:processPropertiesWidth",
		purpose: "preference",
		...cssLength,
		fallback: "",
	}),
	actionPalette: register<string>({
		id: "actionPaletteWidth",
		key: "notitia:sidebar:actionPaletteWidth",
		purpose: "preference",
		...cssLength,
		fallback: "",
	}),
	actionProperties: register<string>({
		id: "actionPropertiesWidth",
		key: "notitia:sidebar:actionPropertiesWidth",
		purpose: "preference",
		...cssLength,
		fallback: "",
	}),
	dataobjectList: register<string>({
		id: "dataobjectListWidth",
		key: "notitia:sidebar:dataobjectListWidth",
		purpose: "preference",
		...cssLength,
		fallback: "",
	}),
	dataobjectProperties: register<string>({
		id: "dataobjectPropertiesWidth",
		key: "notitia:sidebar:dataobjectPropertiesWidth",
		purpose: "preference",
		...cssLength,
		fallback: "",
	}),
	typeProperties: register<string>({
		id: "typePropertiesWidth",
		key: "notitia:sidebar:typePropertiesWidth",
		purpose: "preference",
		...cssLength,
		fallback: "",
	}),
} as const;

/** The id a `SidebarShell` names to have its width remembered. */
export type SidebarWidthId = keyof typeof SIDEBAR_WIDTH;

/** Which edge each editor docks its panels to (⌘B). Same closed key space. */
export const SIDEBAR_SIDE = {
	process: register<"left" | "right">({
		id: "processSidebarSide",
		key: "notitia:sidebar:processSide",
		purpose: "preference",
		...oneOf(["left", "right"] as const),
		fallback: "right",
	}),
	action: register<"left" | "right">({
		id: "actionSidebarSide",
		key: "notitia:sidebar:actionSide",
		purpose: "preference",
		...oneOf(["left", "right"] as const),
		fallback: "right",
	}),
	dataobject: register<"left" | "right">({
		id: "dataobjectSidebarSide",
		key: "notitia:sidebar:dataobjectSide",
		purpose: "preference",
		...oneOf(["left", "right"] as const),
		fallback: "right",
	}),
} as const;

/** The id an editor names to have its docking edge remembered. */
export type SidebarSideId = keyof typeof SIDEBAR_SIDE;

/**
 * Whether each editor's BASE panel — the one docked to the window edge, which has no
 * close button — is collapsed to its rail.
 *
 * Only the manual collapse is stored. A panel the stack collapsed on its own is
 * transient by design: persist that and you reload with the panel that triggered it
 * gone, and find a collapsed panel you never collapsed. See `useSidebarCollapse`.
 *
 * The ids mirror the base panels' width ids, so one panel reads as one row in the
 * privacy panel rather than two unrelated ones.
 */
export const SIDEBAR_COLLAPSED = {
	processOverview: register<boolean>({
		id: "processOverviewCollapsed",
		key: "notitia:sidebar:processOverviewCollapsed",
		purpose: "preference",
		...flag,
		fallback: false,
	}),
	actionPalette: register<boolean>({
		id: "actionPaletteCollapsed",
		key: "notitia:sidebar:actionPaletteCollapsed",
		purpose: "preference",
		...flag,
		fallback: false,
	}),
	dataobjectList: register<boolean>({
		id: "dataobjectListCollapsed",
		key: "notitia:sidebar:dataobjectListCollapsed",
		purpose: "preference",
		...flag,
		fallback: false,
	}),
} as const;

/** The id a view names to have its base panel's collapse remembered. */
export type SidebarCollapsedId = keyof typeof SIDEBAR_COLLAPSED;

/**
 * Whether opening a panel collapses the ones behind it.
 *
 * ONE key for the whole app, unlike `SIDEBAR_SIDE`'s three: how you like panels to
 * behave while you work is a working style, not a per-editor layout, and the
 * control that sets it lives in the account menu rather than in any one editor.
 *
 * On by default. Off, the preference is invisible — nothing in the interface would
 * ever show you that it exists — and the crowding it answers is real: three open
 * panels leave a 1024px window almost no canvas.
 */
const sidebarAutoCollapse = register<boolean>({
	id: "sidebarAutoCollapse",
	key: "notitia:sidebar:autoCollapse",
	purpose: "preference",
	...flag,
	fallback: true,
});

// ── View state ────────────────────────────────────────────────────────────────
// What you were looking at. Cleared on sign-out (`logout()` in `useAuth` clears the `view-state` purpose)
// because a search term can name the previous user's work.

/** A list's order and whether it is reversed — what `HabSortMenu` sets. */
export interface StoredSort {
	mode: string;
	reversed: boolean;
}

/**
 * `"<mode>:<asc|desc>"`, the overviews' sort — `processSidebarSort`'s shape without its
 * filter suffix. Only the SHAPE is checked here: which modes exist is the designer's
 * business, and the comparators that define them live next to the list that renders
 * them (`designer/utils/_shared/overviewSort.ts`), which narrows the mode on the way out.
 */
const sortCodec = {
	parse: (raw: string): StoredSort | undefined => {
		const [mode, direction] = raw.split(":");
		return mode && /^[a-z]+$/.test(mode) ? { mode, reversed: direction === "desc" } : undefined;
	},
	serialize: ({ mode, reversed }: StoredSort): string => `${mode}:${reversed ? "desc" : "asc"}`,
};

/**
 * `"<mode>:<asc|desc>"`, plus `":incomplete"` when the list shows only rows with
 * activities left to attach. The suffix is optional, so a value written before it
 * existed still reads, as "show all". Only the SHAPE is checked here: which modes exist is the designer's business, and the
 * comparators that define them live next to the list that renders them
 * (`designer/utils/process/activityUsageSort.ts`), which narrows the mode on the
 * way out.
 */
const processSidebarSort = register<{ mode: string; reversed: boolean; incompleteOnly: boolean }>({
	id: "processSidebarSort",
	key: "notitia:view:processSidebarSort",
	purpose: "view-state",
	parse: (raw) => {
		const [mode, direction, filter] = raw.split(":");
		return mode
			? { mode, reversed: direction === "desc", incompleteOnly: filter === "incomplete" }
			: undefined;
	},
	serialize: ({ mode, reversed, incompleteOnly }) =>
		`${mode}:${reversed ? "desc" : "asc"}${incompleteOnly ? ":incomplete" : ""}`,
	fallback: { mode: "", reversed: false, incompleteOnly: false },
});

/*
  SEARCH TEXT, one value per scope.

  Scoped, because a filter belongs to the thing it filters: a search typed in
  project A must not be waiting in project B. The entries are otherwise
  identical, so they are built rather than written out — but each still names
  its own id and key, which is what keeps the privacy panel able to say which
  list a stored search belongs to.
*/
function searchEntry(id: string, scoped: boolean) {
	return register<string>({
		id,
		key: `notitia:view:${id}`,
		purpose: "view-state",
		scoped,
		...text,
		fallback: "",
	});
}

const projectSearch = searchEntry("projectSearch", false);
const dataspaceSearch = searchEntry("dataspaceSearch", false);
const processListSearch = searchEntry("processListSearch", true);
const activityListSearch = searchEntry("activityListSearch", true);
const typeListSearch = searchEntry("typeListSearch", true);
const terminologySearch = searchEntry("terminologySearch", true);
const dataobjectViewsSearch = searchEntry("dataobjectViewsSearch", true);
const dataobjectListSearch = searchEntry("dataobjectListSearch", true);
const processDataobjectSearch = searchEntry("processDataobjectSearch", true);
const modelSidebarSearch = searchEntry("modelSidebarSearch", true);

/*
  HOW EACH DESIGNER OVERVIEW IS SORTED, one value per list for the whole app: an
  order you like on one project's processes is how you like processes. The same
  shape as `processSidebarSort`; the comparators are in
  `designer/utils/_shared/overviewSort.ts`, which narrows the mode on the way out.
*/
function sortEntry(id: string) {
	return register<StoredSort>({
		id,
		key: `notitia:view:${id}`,
		purpose: "view-state",
		...sortCodec,
		fallback: { mode: "", reversed: false },
	});
}

const viewListSort = sortEntry("viewListSort");
const processListSort = sortEntry("processListSort");
const activityListSort = sortEntry("activityListSort");
const typeListSort = sortEntry("typeListSort");
const dataobjectListSort = sortEntry("dataobjectListSort");
const terminologySort = sortEntry("terminologySort");
/** The process sidebar's "Available to add" band, sorted apart from its placed rows. */
const processAvailableSort = sortEntry("processAvailableSort");

/*
  HOW A PROJECT'S ACTIVITY LIST IS GROUPED, and which of its sections you folded, per project: the
  groups are the project's, so whether grouping by them helps is too. `auto` (nothing stored) groups
  by tag groups once the project has any; a choice you make is kept. The folded sections are named
  by the key the overview gives each (`designer/utils/activity/tagGroups.ts`) — a group's name, a
  dataobject's code name — and a section not named is open, so a new group arrives open.
*/
const activityListGrouping = register<"auto" | "none" | "tags" | "dataobject">({
	id: "activityListGrouping",
	key: "notitia:view:activityListGrouping",
	purpose: "view-state",
	scoped: true,
	...oneOf(["auto", "none", "tags", "dataobject"] as const),
	fallback: "auto",
});

/** As many folded sections as anyone has a use for; past it, the oldest fold is forgotten. */
const FOLDED_SECTIONS_LIMIT = 100;

const activityListFolded = register<string[]>({
	id: "activityListFolded",
	key: "notitia:view:activityListFolded",
	purpose: "view-state",
	scoped: true,
	...jsonCodec(
		(value) => stringList(value)?.slice(-FOLDED_SECTIONS_LIMIT),
		(value: string[]) => value.slice(-FOLDED_SECTIONS_LIMIT),
	),
	fallback: [],
});

/**
 * How many entries a recently-opened list keeps. Enough to sort the things you
 * are working on to the top, too few to be a history of what you did.
 */
export const RECENTLY_OPENED_LIMIT = 20;

/** A JSON array of names, most recent first. Anything else reads as absent. */
const nameList = jsonCodec(
	(value) => stringList(value)?.slice(0, RECENTLY_OPENED_LIMIT),
	(value: string[]) => value.slice(0, RECENTLY_OPENED_LIMIT),
);

/*
  WHAT YOU OPENED LAST, per project and per overview, so "Recently opened" can
  sort it first. The one view-state entry that is a record of what you did rather
  than a setting you made; `docs/privacy-storage.md` says why it still needs no
  consent. Written when you open an editor, capped, never sent anywhere, and
  cleared on sign-out with the rest of the view state, since it names the
  previous user's work.
*/
function recentEntry(id: string) {
	return register<string[]>({
		id,
		key: `notitia:view:${id}`,
		purpose: "view-state",
		scoped: true,
		...nameList,
		fallback: [],
	});
}

const recentViews = recentEntry("recentViews");
const recentProcesses = recentEntry("recentProcesses");
const recentActivities = recentEntry("recentActivities");
const recentTypes = recentEntry("recentTypes");
const recentDataobjects = recentEntry("recentDataobjects");

/*
  WHICH TAB YOU WERE ON.

  The vocabularies are declared here rather than at the refs that hold them, and
  the surfaces import the types back — the alternative is the same union written
  twice, where a value added in one place restores as a fallback in the other.
  What does NOT live here is vocabulary that carries behaviour: the sidebar's
  sort modes define comparators, so they stay next to the list that renders them
  and `processSidebarSort` below only checks the stored SHAPE.
*/
const PROCESS_SIDEBAR_TABS = ["activities", "relations", "embedded"] as const;
export type ProcessSidebarTab = (typeof PROCESS_SIDEBAR_TABS)[number];

const PROJECT_HOME_TABS = ["overview", "settings"] as const;
export type ProjectHomeTab = (typeof PROJECT_HOME_TABS)[number];

const PROJECT_HOME_SECTIONS = [
	"general",
	"languages",
	"build",
	"releases",
	"dependencies",
	"codenames",
	"utilities",
	"entryPage",
	"history",
	"git",
	"members",
	"department",
	"roles",
] as const;
export type ProjectHomeSection = (typeof PROJECT_HOME_SECTIONS)[number];

const DATAOBJECTS_TABS = ["views", "dataobjects"] as const;
export type DataobjectsTab = (typeof DATAOBJECTS_TABS)[number];

const DATAOBJECT_LIST_ROWS = ["dataobjects", "attributes"] as const;
export type DataobjectListRows = (typeof DATAOBJECT_LIST_ROWS)[number];

const processSidebarTab = register<ProcessSidebarTab>({
	id: "processSidebarTab",
	key: "notitia:view:processSidebarTab",
	purpose: "view-state",
	scoped: true,
	...oneOf(PROCESS_SIDEBAR_TABS),
	fallback: "activities",
});

const projectHomeTab = register<ProjectHomeTab>({
	id: "projectHomeTab",
	key: "notitia:view:projectHomeTab",
	purpose: "view-state",
	scoped: true,
	...oneOf(PROJECT_HOME_TABS),
	fallback: "overview",
});

const projectHomeSection = register<ProjectHomeSection>({
	id: "projectHomeSection",
	key: "notitia:view:projectHomeSection",
	purpose: "view-state",
	scoped: true,
	...oneOf(PROJECT_HOME_SECTIONS),
	fallback: "general",
});

const dataobjectsTab = register<DataobjectsTab>({
	id: "dataobjectsTab",
	key: "notitia:view:dataobjectsTab",
	purpose: "view-state",
	scoped: true,
	...oneOf(DATAOBJECTS_TABS),
	fallback: "views",
});

/** Whether the Dataobjects table lists dataobjects, or their attributes and relations. */
const dataobjectListRows = register<DataobjectListRows>({
	id: "dataobjectListRows",
	key: "notitia:view:dataobjectListRows",
	purpose: "view-state",
	scoped: true,
	...oneOf(DATAOBJECT_LIST_ROWS),
	fallback: "dataobjects",
});

/** A slot of the process view, as `operatorPanelTabs` names it: `right/top`. */
const SLOT_KEY = new RegExp(`^(${PANEL_COLUMNS.join("|")})/(${PANEL_ALIGNS.join("|")})$`);

/**
 * Which panel each slot of a process view shows, where two or more share it as tabs — by the slot
 * (`middle/top`) and the panel's interactable id. Per process INSTANCE (scope `<dataspace>/<process>`):
 * the ids are that instance's, and the same instance keeps them through a reload or a restart. An id
 * that is gone is not shown, and the slot shows its first panel (`activeTab`); it is left, in case the
 * panel comes back. Only a choice the reader makes, or a panel the process opens, is written.
 */
const operatorPanelTabs = register<Record<string, string>>({
	id: "operatorPanelTabs",
	key: "notitia:view:operatorPanelTabs",
	purpose: "view-state",
	scoped: true,
	...jsonCodec((value) => {
		const fields = record(value);
		if (!fields) return undefined;
		const out: Record<string, string> = {};
		for (const [slot, id] of Object.entries(fields)) {
			if (SLOT_KEY.test(slot) && typeof id === "string" && id) out[slot] = id;
		}
		return out;
	}),
	fallback: {},
});

/** The view-state slice — what `usePersistedView` binds a surface to. */
export const VIEW_STATE = {
	processSidebarSort,
	processAvailableSort,
	projectSearch,
	dataspaceSearch,
	processListSearch,
	activityListSearch,
	typeListSearch,
	terminologySearch,
	dataobjectViewsSearch,
	dataobjectListSearch,
	processDataobjectSearch,
	modelSidebarSearch,
	processSidebarTab,
	projectHomeTab,
	projectHomeSection,
	dataobjectsTab,
	dataobjectListRows,
	operatorPanelTabs,
	viewListSort,
	processListSort,
	activityListSort,
	activityListGrouping,
	activityListFolded,
	typeListSort,
	dataobjectListSort,
	terminologySort,
	recentViews,
	recentProcesses,
	recentActivities,
	recentTypes,
	recentDataobjects,
} as const;

/** Everything, by name. `STORAGE.themeMode.read()`. */
export const STORAGE = {
	storageVersion,
	pendingInvitation,
	themeMode,
	locale,
	logHeight,
	operatorPanelSplits,
	envNameColWidth,
	sidebarWidth: SIDEBAR_WIDTH,
	sidebarSide: SIDEBAR_SIDE,
	sidebarCollapsed: SIDEBAR_COLLAPSED,
	sidebarAutoCollapse,
	view: VIEW_STATE,
} as const;

// ── The rename ────────────────────────────────────────────────────────────────

/**
 * The keys as they were before this table existed: `auth_token`, `nta_*` and a
 * run of kebab-case sidebar keys, three conventions in all. `notitia:<area>:<name>`
 * won because it is the only one with room for a `:<scopeId>` segment.
 *
 * Copy, then delete, guarded by a version marker so it happens once. The access
 * token no longer touches the device at all: `auth_token` and its renamed
 * `notitia:auth:token` are deleted, never carried over (`RETIRED_KEYS`).
 */
const LEGACY_KEYS: Record<string, string> = {
	nta_theme_mode: "notitia:app:themeMode",
	nta_locale: "notitia:app:locale",
	"process-sidebar-sort": "notitia:view:processSidebarSort",
	"process-sidebar-overview-width": "notitia:sidebar:processOverviewWidth",
	"process-sidebar-properties-width": "notitia:sidebar:processPropertiesWidth",
	"action-sidebar-palette-width": "notitia:sidebar:actionPaletteWidth",
	"action-sidebar-properties-width": "notitia:sidebar:actionPropertiesWidth",
	"dataobject-sidebar-list-width": "notitia:sidebar:dataobjectListWidth",
	"dataobject-sidebar-properties-width": "notitia:sidebar:dataobjectPropertiesWidth",
	"process-sidebar-side": "notitia:sidebar:processSide",
	"action-sidebar-side": "notitia:sidebar:actionSide",
	"dataobject-sidebar-side": "notitia:sidebar:dataobjectSide",
};

/**
 * Keys a build no longer reads, removed rather than left behind. An entry that
 * leaves the table also leaves the privacy panel — so without this its bytes would
 * sit on the device with nothing listing them and "clear local data" not reaching
 * them, the one thing the table exists to rule out. A scoped key goes with every
 * scope it was written under.
 *
 * - `notitia:view:dataspaceHomeTab` (version 2): which tab a dataspace page had
 *   open. The tabs became routes — the URL says it now.
 * - `auth_token` and its renamed `notitia:auth:token` (version 3): the access
 *   token. It lives in memory only now and never touches the device.
 * - `notitia:process:questionsWidth`, `notitia:process:chatQuestionsSplit` and
 *   `notitia:view:operatorProcessTab` (version 4): the question list's width, the
 *   split between a chat and the questions, and a process's centre tab. The process
 *   view became one grid of panels, divided by `operatorPanelSplits`, its slots'
 *   tabs remembered by `operatorPanelTabs`.
 */
const RETIRED_KEYS = [
	"notitia:view:dataspaceHomeTab",
	"auth_token",
	"notitia:auth:token",
	"notitia:process:questionsWidth",
	"notitia:process:chatQuestionsSplit",
	"notitia:view:operatorProcessTab",
];

const STORAGE_VERSION = "4";

export function migrateLegacyKeys(): void {
	if (storageVersion.read() === STORAGE_VERSION) return;
	for (const [legacy, next] of Object.entries(LEGACY_KEYS)) {
		const value = safeGet(legacy);
		// An existing new-name value wins: it was written by this build and is
		// therefore newer than whatever the old name still holds.
		if (value !== null && safeGet(next) === null) safeSet(next, value);
		safeRemove(legacy);
	}
	for (const retired of RETIRED_KEYS) {
		safeRemove(retired);
		safeKeys()
			.filter((key) => key.startsWith(`${retired}:`))
			.forEach(safeRemove);
	}
	storageVersion.write(STORAGE_VERSION);
}

/*
  RUN AT IMPORT, not from `main.ts`.

  `useAuth`, `useAppTheme` and `i18n/index.ts` all read storage while their
  MODULE evaluates, and module evaluation finishes before `main.ts`'s first
  statement runs — so a call from there would always be too late and every one
  of them would read an empty new-name key. Every reader imports this module, so
  doing it here is what actually orders the migration before the first read.
*/
migrateLegacyKeys();
